import {
    Mesh,
    MeshBasicMaterial,
    DoubleSide,
    FrontSide
} from 'three';
import gsap from 'gsap/all';

import { VRObject3D } from './VRObject3D';

import {
    moodCircleGeometry,
    chairCircleGeometry,
    green_mood,
    yellow_mood,
    red_mood,
    borderCircleGeometry,
    inTalkTexture,
    bagless_logo,
    smallChairCircleGeometry,
    cameraTexture,
    crossedCameraTexture,
    crossedMicTexture,
    micTexture,
    btnGeometry,
    bigBtnGeometry,
    playIconTexture,
    materialPrimary,
} from './helpers/texturesAndGeometries';

import { WEBRTC_EVENTS } from '../../constants/webrtc';
import { SCENE_EVENTS } from '../MVC/Model';
import { GLOBAL_EVENTS } from '../../constants/global';

class VRProfileHelper extends VRObject3D {

    constructor(containerName, camera, texture, model) {
        super(containerName);

        this.model = model;
        this.userId = null;
        this.userInfo = null;
        this.userData = null;

        this.mood = null;

        this.camera = camera;

        this.borderMaterial = new MeshBasicMaterial({ side: DoubleSide, depthWrite: false, transparent: true, color: 0xffffff, opacity: 0.3 });
        this.material = new MeshBasicMaterial({ map: texture, side: DoubleSide, transparent: true });
        return this;
    }

    getUserInfo() {
        return this.userInfo || this.userData;
    }

    hideUserOptions() {
        $("#room-owner-menu").removeClass("is-active");
    }

    addAsFriend() {
        this.model.worldFetcher.addAsFriend(this.userId, (response) => {
            this.model.onFriendAddedSuccess(response);
        });
    }

    initiateVideoCall() {
        this.model.dispatchEvent({ type: WEBRTC_EVENTS.WEBRTC_VIDEO_ONE_ON_ONE, data: { item: this.userData } });
        this.hideUserOptions();
    }

    initiateChat() {
        this.model.dispatchEvent({ type: SCENE_EVENTS.ONE_ON_ONE_CHAT, item: this.userData });
        this.hideUserOptions();
    }

    showOrUpdateMood(index) {
        this.mood = parseInt(index);

        let moodTexture = null;
        switch (this.mood) {
            case 1: moodTexture = green_mood; break;
            case 2: moodTexture = yellow_mood; break;
            case 3: moodTexture = red_mood; break;
        }

        if (moodTexture !== null) {
            this.moodMaterial.map = moodTexture;
            this.moodMesh.visible = true;
            if (this.audioAnalyser) {
                // this.audioAnalyser.outerCircle.material.map = moodTexture;
                // this.audioAnalyser.outerCircle.material.needsUpdate = true;
            }

            if (this.newMoodMesh) {
                this.newMoodMesh.material.map = moodTexture;
                this.newMoodMesh.material.needsUpdate = true;
            }
        }
    }

    addBusyOnCall() {
        this.busyMaterial = new MeshBasicMaterial({ map: inTalkTexture, side: DoubleSide, transparent: true, depthWrite: false });
        this.busyMesh = new Mesh(chairCircleGeometry, this.busyMaterial);
        this.busyMesh.type = 'busy';
        this.busyMesh.name = 'busy';
        this.busyMesh.renderOrder = 5;
        this.busyMesh.visible = false;
        this.busyMesh.translateZ(1);
        this.mesh.add(this.busyMesh);
    }

    addMood() {
        this.moodMaterial = new MeshBasicMaterial({ map: green_mood, side: DoubleSide, depthWrite: false, transparent: true });
        this.moodMesh = new Mesh(moodCircleGeometry, this.moodMaterial);
        this.moodMesh.type = 'mood';
        this.moodMesh.name = 'mood';
        this.moodMesh.renderOrder = 0; // Behind chair mesh
        this.moodMesh.visible = false;
        this.moodMesh.translateZ(-5);
        this.mesh.add(this.moodMesh);
    }

    previewAvatar() {
        this.previewAvatarMaterial = new MeshBasicMaterial({ color: 0x123456, side: DoubleSide, depthWrite: true, transparent: false });
        this.previewAvatarMesh = new Mesh(chairCircleGeometry, this.previewAvatarMaterial);
        this.previewAvatarMesh.type = 'previewAvatarMesh';
        this.previewAvatarMesh.name = 'previewAvatarMesh';
        this.previewAvatarMesh.renderOrder = 10;
        this.previewAvatarMesh.visible = true;
        this.previewAvatarMesh.translateZ(2);
        this.mesh.add(this.previewAvatarMesh);
    }

    addLoadingMesh() {
        this.loadingMaterial = new MeshBasicMaterial({ map: bagless_logo, side: DoubleSide, depthWrite: true, transparent: false });
        this.loadingMesh = new Mesh(moodCircleGeometry, this.loadingMaterial);
        this.loadingMesh.type = 'loading';
        this.loadingMesh.name = 'loading';
        this.loadingMesh.visible = false;
        this.loadingMesh.renderOrder = 20;
        this.add(this.loadingMesh);
        this.loadingMesh.translateZ(2);
    }

    addBorder() {
        this.borderMesh = new Mesh(borderCircleGeometry, this.borderMaterial);
        this.borderMesh.renderOrder = -1;
        this.borderMesh.translateZ(-6);
        this.mesh.add(this.borderMesh);
    }

    addMicIcon() {
        const position = {
            "x": 0,
            "y": -39,
            "z": 15
        };
        this.crossMicMaterial = new MeshBasicMaterial({ map: crossedMicTexture, side: FrontSide, depthWrite: true, transparent: true });
        this.micMaterial = new MeshBasicMaterial({ map: micTexture, side: FrontSide, depthWrite: true, transparent: true });
        this.micMesh = new Mesh(smallChairCircleGeometry, this.micMaterial);
        this.micMesh.renderOrder = -1;
        this.micMesh.translateZ(-6);
        this.micMesh.translateX(6);
        this.micMesh.translateY(54);
        this.micMesh.position.set(position.x, position.y, position.z);
        this.micMesh.renderOrder = 3;

        this.micMoodMesh = new Mesh(smallChairCircleGeometry, materialPrimary);
        this.micMoodMesh.scale.set(1.1, 1.1, 1);
        this.micMoodMesh.renderOrder = -2;
        this.micMoodMesh.translateZ(-9);
        this.micMoodMesh.translateX(6);
        this.micMoodMesh.translateY(54);
        this.micMoodMesh.position.set(position.x, position.y, position.z);
        this.micMoodMesh.renderOrder = 2;

        this.micMesh.type = "seatMic";
        this.micMoodMesh.type = "seatMic";
    }

    addCameraIcon() {
        const position = {
            "x": 39,
            "y": 3,
            "z": 15
        };
        this.crossCameraMaterial = new MeshBasicMaterial({ map: crossedCameraTexture, side: DoubleSide, depthWrite: true, transparent: true });
        this.cameraMaterial = new MeshBasicMaterial({ map: cameraTexture, side: DoubleSide, depthWrite: true, transparent: true });
        this.cameraMesh = new Mesh(smallChairCircleGeometry, this.cameraMaterial);
        this.cameraMesh.renderOrder = -1;
        this.cameraMesh.translateZ(-6);
        this.cameraMesh.translateX(-54);
        this.cameraMesh.translateY(-2);
        this.cameraMesh.position.set(position.x, position.y, position.z + 1);
        this.cameraMesh.renderOrder = 3;

        this.cameraMoodMesh = new Mesh(smallChairCircleGeometry, materialPrimary);
        this.cameraMoodMesh.scale.set(1.1, 1.1, 1);
        this.cameraMoodMesh.renderOrder = -2;
        this.cameraMoodMesh.translateZ(-9);
        this.cameraMoodMesh.translateX(-54);
        this.cameraMoodMesh.translateY(-2);
        this.cameraMoodMesh.position.set(position.x, position.y, position.z);
        this.cameraMoodMesh.renderOrder = 2;

        this.cameraMesh.type = "seatCamera";
        this.cameraMoodMesh.type = "seatCamera";
    }

    animateFirstSeat() {
        if (this.animateFirstSeatAnimationStarted) return;
        gsap.killTweensOf(this.mesh.scale);
        gsap.from(this.mesh.scale, {
            duration: 0.8,
            x: 0,
            y: 0,
            ease: "expo.in",
        });
        gsap.from(this.moodMesh.scale, {
            duration: 1,
            delay: 0.5,
            x: 1.3,
            y: 1.3,
            ease: "bounce.out",
        });
        this.animateMic();
        this.animateCamera();
        this.animateFirstSeatAnimationStarted = true;
    }

    animateMic() {
        gsap.killTweensOf(this.micMoodMesh.scale);
        this.micMoodMesh.scale.set(1, 1, 1);
        gsap.from(this.micMoodMesh.scale, {
            duration: 1.1,
            delay: 0.3,
            x: 2,
            y: 2,
            ease: "bounce.out",
        });
    }

    animateCamera() {
        gsap.killTweensOf(this.cameraMoodMesh.scale);
        this.cameraMoodMesh.scale.set(1, 1, 1);
        gsap.from(this.cameraMoodMesh.scale, {
            duration: 1,
            delay: 0.6,
            x: 2,
            y: 2,
            ease: "bounce.out",
        });
    }

    animateCameraClick() {
        gsap.killTweensOf(this.cameraMoodMesh.scale);
        this.cameraMoodMesh.scale.set(1, 1, 1);
        gsap.from(this.cameraMoodMesh.scale, {
            duration: 0.5,
            x: 1.30,
            y: 1.30,
            z: 1.30,
            ease: "bounce.out",
        });
    }

    animateMicClick() {
        gsap.killTweensOf(this.micMoodMesh.scale);
        this.micMoodMesh.scale.set(1, 1, 1);
        gsap.from(this.micMoodMesh.scale, {
            duration: 0.5,
            x: 1.30,
            y: 1.30,
            z: 1.30,
            ease: "bounce.out",
        });
    }
}

export { VRProfileHelper };